import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  VoiceCapabilityBadge, 
  IntegrationBadges, 
  CapabilityModeBadge,
  RequiresWizardIndicator,
  VOICE_CAPABILITIES 
} from './AgentCapabilityBadges';
import { usePlan } from '../context/PlanContext';
import { TemplateIcon } from '../utils/iconResolver';
import UpgradeModal from './UpgradeModal';

import {
  ArrowRight,
  CheckCircle,
  CircleNotch,
  Export,
  FileArrowUp,
  GearSix,
  Info,
  Lightning,
  LinkSimple,
  ListChecks,
  Microphone,
  Sparkle,
  SpeakerHigh,
  Target,
  WarningCircle,
  Waveform,
  WebhooksLogo,
  X
} from '@phosphor-icons/react';
/**
 * Simple Markdown Renderer for Agent Descriptions
 * Handles: **bold**, - bullet points, line breaks
 */
const MarkdownDescription = ({ text }) => {
  if (!text) return null;
  
  // Split by double line breaks or markdown sections
  const sections = text.split(/\n\n|\*\*(?=[A-Z])/);
  
  return (
    <div className="space-y-4">
      {text.split(/(?=\*\*[A-Z][^*]+\*\*)/g).map((section, sectionIdx) => {
        // Check if section starts with a header (**Header**)
        const headerMatch = section.match(/^\*\*([^*]+)\*\*([\s\S]*)/);
        
        if (headerMatch) {
          const headerText = headerMatch[1].trim();
          const content = headerMatch[2].trim();
          
          // Check if content has bullet points
          const hasBullets = content.includes(' - ') || content.startsWith('- ');
          
          if (hasBullets) {
            // Parse bullet points
            const bullets = content.split(/\s+-\s+/).filter(b => b.trim());
            return (
              <div key={sectionIdx}>
                <h4 className="text-sm font-semibold text-white mb-2">{headerText}</h4>
                <ul className="space-y-2">
                  {bullets.map((bullet, bulletIdx) => {
                    // Handle nested bold within bullets
                    const parts = bullet.split(/\*\*([^*]+)\*\*/g);
                    return (
                      <li key={bulletIdx} className="flex items-start gap-2 text-sm text-gray-300">
                        <span className="text-[#6366F1] mt-1">•</span>
                        <span>
                          {parts.map((part, partIdx) => 
                            partIdx % 2 === 1 
                              ? <strong key={partIdx} className="text-white font-medium">{part}</strong>
                              : part
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          } else {
            // Plain text content after header
            // Handle bold within content
            const parts = content.split(/\*\*([^*]+)\*\*/g);
            return (
              <div key={sectionIdx}>
                <h4 className="text-sm font-semibold text-white mb-2">{headerText}</h4>
                <p className="text-sm text-gray-300 leading-relaxed">
                  {parts.map((part, partIdx) => 
                    partIdx % 2 === 1 
                      ? <strong key={partIdx} className="text-white font-medium">{part}</strong>
                      : part
                  )}
                </p>
              </div>
            );
          }
        } else {
          // No header - just render as paragraph with bold support
          if (section.trim()) {
            const parts = section.split(/\*\*([^*]+)\*\*/g);
            return (
              <p key={sectionIdx} className="text-sm text-gray-300 leading-relaxed">
                {parts.map((part, partIdx) => 
                  partIdx % 2 === 1 
                    ? <strong key={partIdx} className="text-white font-medium">{part}</strong>
                    : part
                )}
              </p>
            );
          }
          return null;
        }
      })}
    </div>
  );
};

/**
 * Agent Detail Modal - Marketplace Agent Preview
 * Shows comprehensive agent information before installation
 * Uses existing blueprint + contract fields (read-only)
 * 
 * PUBLIC BETA: Updated with new voice capability classification
 */
const AgentDetailModal = ({ 
  template, 
  isOpen, 
  onClose, 
  onInstall, 
  installing 
}) => {
  const { userPlan } = usePlan();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Determine if user is on Basic plan (needs feature locking)
  const isBasicPlan = !userPlan || userPlan === 'basic' || userPlan === 'starter';
  
  if (!isOpen || !template) return null;

  // V1.3 PUBLIC BETA: Extract contract data including new voice capability fields
  // Template fields are now the primary source, agent_config/blueprint are fallbacks
  
  // Identity (from template directly)
  const name = template.name || 'Agent';
  const description = template.description || template.short_description || '';
  const category = template.category || 'General';
  const iconStr = template.icon || 'bot';
  
  // Goals (from config for compatibility)
  const config = template.config || {};
  const agentConfig = template.agent_config || {};
  const goals = agentConfig.goals || {};
  const primaryGoal = goals.primary_goal || config.primary_goal || '';
  
  // Context & Responsibilities
  const context = agentConfig.context_and_background || {};
  const keyResponsibilities = context.key_responsibilities || [];
  
  // V1.3 PUBLIC BETA: Voice Capability Classification
  const voiceCapability = template.voice_capability || agentConfig.capabilities?.voice_capability || 'none';
  const agentType = template.agent_type || 'text';
  const requiresWizard = template.requires_wizard || false;
  const wizardType = template.wizard_type;
  
  // V1.2 Capabilities - Read from template root first (DB source of truth)
  const mode = template.capability_mode || agentConfig.capabilities?.mode || 'text_only';
  const inputTypes = template.input_types || agentConfig.capabilities?.input_types || ['text'];
  const outputTypes = template.output_types || agentConfig.capabilities?.output_types || ['markdown_report'];
  
  // V1.2 Integration Requirements - Read from template root first
  // INTEGRATION SEMANTICS:
  // - required: Agent NEEDS this integration to function (triggers "Needs Integration" badge)
  // - optional: Agent works better with this but can function without it
  // - compatible: Agent can output to this system but doesn't depend on it
  const templateIntReqs = template.integration_requirements || {};
  const configIntReqs = agentConfig.integration_requirements || {};
  const requiredIntegrations = templateIntReqs.required || configIntReqs.required || [];
  const optionalIntegrations = templateIntReqs.optional || configIntReqs.optional || [];
  const compatibleIntegrations = templateIntReqs.compatible || configIntReqs.compatible || [];
  const integrationsEnabled = template.integrations_enabled || [];
  const externalSupported = template.external_automation_supported ?? templateIntReqs.external_automation_supported ?? configIntReqs.external_automation_supported ?? false;
  const externalRecommended = template.external_automation_recommended ?? templateIntReqs.external_automation_recommended ?? configIntReqs.external_automation_recommended ?? false;
  const externalExamples = template.external_automation_examples || templateIntReqs.external_automation_examples || configIntReqs.external_automation_examples || ['Zapier', 'n8n', 'Make'];
  const minimumViable = templateIntReqs.minimum_viable_without_integrations || configIntReqs.minimum_viable_without_integrations || '';
  
  // V1.2 Automation Contract - Read from template root first
  const nativeActions = template.native_actions || agentConfig.automation_contract?.native_actions || [];
  
  // V1.3 PUBLIC BETA: Voice capability badge info
  const voiceCapabilityInfo = VOICE_CAPABILITIES[voiceCapability] || VOICE_CAPABILITIES.none;
  
  // Helper to get capability badge style
  const getCapabilityBadge = () => {
    switch (mode) {
      case 'integration_dependent':
        return { 
          label: 'Integration Dependent', 
          className: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
          description: 'Requires connected integrations for full functionality'
        };
      case 'automation_ready':
        return { 
          label: 'Automation Ready', 
          className: 'bg-green-500/20 text-green-400 border-green-500/30',
          description: 'Produces structured output ready for automation workflows'
        };
      case 'file_enabled':
        return { 
          label: 'File Enabled', 
          className: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
          description: 'Can process file inputs and attachments'
        };
      default:
        return { 
          label: 'Text Only', 
          className: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
          description: 'Works with text inputs and produces formatted reports'
        };
    }
  };

  const capabilityBadge = getCapabilityBadge();
  
  // Format input/output type for display
  const formatType = (type) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-2xl md:max-h-[85vh] z-50 flex flex-col">
        <div className="glass rounded-2xl border border-[rgba(255,255,255,0.2)] shadow-2xl overflow-hidden flex flex-col max-h-full">
          
          {/* Header */}
          <div className="p-6 border-b border-[rgba(255,255,255,0.1)] flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-xl flex items-center justify-center flex-shrink-0">
                  <TemplateIcon icon={iconStr} size={28} className="text-white" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white mb-1">{name}</h2>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs px-2 py-1 bg-[rgba(255,255,255,0.05)] text-gray-300 rounded">
                      {category}
                    </span>
                    {/* V1.3: Voice Capability Badge */}
                    {voiceCapability !== 'none' && (
                      <VoiceCapabilityBadge voiceCapability={voiceCapability} size="sm" />
                    )}
                    {/* Capability Mode Badge */}
                    <CapabilityModeBadge mode={mode} size="sm" />
                    {/* Wizard Required Indicator */}
                    {requiresWizard && (
                      <RequiresWizardIndicator requiresWizard={requiresWizard} wizardType={wizardType} />
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-[rgba(255,255,255,0.1)] rounded-lg transition-colors"
              >
                <X size={20} className="text-gray-400" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            
            {/* V1.3: Voice Capability Info Box (for voice agents) */}
            {voiceCapability !== 'none' && (
              <section className="p-4 bg-purple-500/10 border border-purple-500/20 rounded-xl">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                    {voiceCapability === 'live_voice_s2s' && <Microphone size={20} className="text-purple-400" />}
                    {voiceCapability === 'voice_output_tts' && <SpeakerHigh size={20} className="text-blue-400" />}
                    {voiceCapability === 'voice_input' && <Waveform size={20} className="text-teal-400" />}
                  </div>
                  <div>
                    <h4 className="font-medium text-white">{voiceCapabilityInfo.label}</h4>
                    <p className="text-sm text-gray-400 mt-1">{voiceCapabilityInfo.description}</p>
                    {requiresWizard && (
                      <p className="text-xs text-amber-400 mt-2 flex items-center gap-1">
                        <WarningCircle size={12} />
                        This agent requires guided setup wizard with opening message and voice configuration
                      </p>
                    )}
                  </div>
                </div>
              </section>
            )}
            
            {/* What This Agent Does */}
            <section>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Sparkle size={16} className="text-[#6366F1]" />
                What This Agent Does
              </h3>
              <div className="text-sm text-gray-300 leading-relaxed mb-3">
                <MarkdownDescription text={description} />
              </div>
              
              {primaryGoal && (
                <div className="flex items-start gap-2 p-3 bg-[rgba(99,102,241,0.08)] border border-[rgba(99,102,241,0.2)] rounded-lg">
                  <Target size={16} className="text-[#6366F1] mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="text-xs font-medium text-[#6366F1]">Primary Goal</span>
                    <p className="text-sm text-gray-300 mt-1">{primaryGoal}</p>
                  </div>
                </div>
              )}
              
              {keyResponsibilities.length > 0 && (
                <div className="mt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <ListChecks size={14} className="text-gray-400" />
                    <span className="text-xs font-medium text-gray-400">Key Responsibilities</span>
                  </div>
                  <ul className="space-y-1.5">
                    {keyResponsibilities.slice(0, 5).map((resp, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                        {resp}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            {/* Capabilities & Automation */}
            <section>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <Lightning size={16} className="text-[#6366F1]" />
                Capabilities & Automation
              </h3>
              
              {/* Mode Explanation */}
              <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className={`w-2 h-2 rounded-full ${
                    mode === 'integration_dependent' ? 'bg-amber-500' :
                    mode === 'automation_ready' ? 'bg-green-500' : 'bg-blue-500'
                  }`} />
                  <span className="text-sm font-medium text-white">{capabilityBadge.label}</span>
                </div>
                <p className="text-xs text-gray-400">{capabilityBadge.description}</p>
              </div>
              
              {/* Required Integrations */}
              {requiredIntegrations.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <WarningCircle size={14} className="text-amber-400" />
                    <span className="text-xs font-medium text-amber-400">Required Integrations</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {requiredIntegrations.map((integration, idx) => (
                      <span 
                        key={idx}
                        className="px-3 py-1.5 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-400 text-sm font-medium flex items-center gap-2"
                      >
                        <LinkSimple size={12} />
                        {integration.charAt(0).toUpperCase() + integration.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Optional Integrations */}
              {optionalIntegrations.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-400">Optional Integrations</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {optionalIntegrations.map((integration, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded text-gray-400 text-xs"
                      >
                        {integration.charAt(0).toUpperCase() + integration.slice(1)}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Compatible Integrations */}
              {compatibleIntegrations.length > 0 && (
                <div className="mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-medium text-gray-500">Compatible With</span>
                    <Info size={12} className="text-gray-600" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {compatibleIntegrations.map((integration, idx) => (
                      <span 
                        key={idx}
                        className="px-2 py-1 bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)] rounded text-gray-500 text-xs"
                      >
                        {integration.charAt(0).toUpperCase() + integration.slice(1).replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* External Automation Support */}
              {externalSupported && (
                <div className="p-3 bg-[rgba(139,92,246,0.08)] border border-[rgba(139,92,246,0.2)] rounded-lg mb-3">
                  <div className="flex items-start gap-2">
                    <WebhooksLogo size={16} className="text-[#8B5CF6] mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-sm font-medium text-white">
                        External Automation Supported
                        {externalRecommended && (
                          <span className="ml-2 px-2 py-0.5 bg-[rgba(139,92,246,0.3)] text-[#8B5CF6] text-xs rounded">
                            Recommended
                          </span>
                        )}
                      </span>
                      <p className="text-xs text-gray-400 mt-1">
                        Works with {externalExamples.join(', ')} via webhook
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Works Without Integrations */}
              {minimumViable && (
                <div className="p-3 bg-[rgba(34,197,94,0.08)] border border-[rgba(34,197,94,0.2)] rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="text-xs font-medium text-green-400">Works Without Integrations</span>
                      <p className="text-xs text-gray-400 mt-1">{minimumViable}</p>
                    </div>
                  </div>
                </div>
              )}
            </section>

            {/* Inputs & Outputs */}
            <section>
              <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                <ArrowRight size={16} className="text-[#6366F1]" />
                Inputs & Outputs
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                {/* Inputs */}
                <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <FileArrowUp size={14} className="text-blue-400" />
                    <span className="text-xs font-medium text-gray-400">Input Types</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {inputTypes.map((type, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-blue-500/10 text-blue-400 text-xs rounded">
                        {formatType(type)}
                      </span>
                    ))}
                  </div>
                </div>
                
                {/* Outputs */}
                <div className="p-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Export size={14} className="text-purple-400" />
                    <span className="text-xs font-medium text-gray-400">Output Types</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {outputTypes.map((type, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-500/10 text-purple-400 text-xs rounded">
                        {formatType(type)}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Action Payload indicator */}
              {(mode === 'automation_ready' || mode === 'integration_dependent') && (
                <div className="mt-3 flex items-center gap-2 text-xs text-gray-400">
                  <CheckCircle size={12} className="text-green-500" />
                  Produces structured <code className="px-1 py-0.5 bg-[rgba(255,255,255,0.05)] rounded">action_payload</code> for automation
                </div>
              )}
            </section>

            {/* Example Automation (Read-only) */}
            {nativeActions.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                  <GearSix size={16} className="text-[#6366F1]" />
                  Available Automation Actions
                </h3>
                <div className="space-y-2">
                  {nativeActions.slice(0, 3).map((action, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center gap-3 p-2 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] rounded-lg"
                    >
                      <Lightning size={14} className="text-[#6366F1]" />
                      <span className="text-sm text-white">
                        {action.label || `${action.provider}.${action.action}`}
                      </span>
                      {action.description && (
                        <span className="text-xs text-gray-500">— {action.description}</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t border-[rgba(255,255,255,0.1)] flex-shrink-0">
            <div className="flex flex-wrap gap-3">
              {/* Install Agent Button - Locked for Basic */}
              {isBasicPlan ? (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="group relative flex-1 min-w-[150px] px-6 py-3 bg-gradient-to-r from-[#6366F1]/40 to-[#8B5CF6]/40 text-white/60 rounded-lg flex items-center justify-center gap-2 font-medium cursor-pointer hover:from-[#6366F1]/50 hover:to-[#8B5CF6]/50 hover:text-white/80 transition-all"
                >
                  <Lightning size={18} />
                  <span className="group-hover:opacity-0 transition-opacity">Install Agent</span>
                  <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm">
                    Upgrade to unlock
                  </span>
                </button>
              ) : (
                <button
                  onClick={() => onInstall(template.id, template.name)}
                  disabled={installing}
                  className="flex-1 min-w-[150px] px-6 py-3 bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white rounded-lg hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
                >
                  {installing ? (
                    <>
                      <CircleNotch size={18} className="animate-spin" />
                      Installing...
                    </>
                  ) : (
                    <>
                      <Lightning size={18} />
                      Install Agent
                    </>
                  )}
                </button>
              )}
              
              {/* Integrations Button - Locked for Basic */}
              {isBasicPlan ? (
                <button
                  onClick={() => setShowUpgradeModal(true)}
                  className="group relative px-4 py-3 bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.1)] text-gray-500 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-[rgba(255,255,255,0.08)] hover:text-gray-300 transition-all"
                >
                  <GearSix size={16} />
                  <span className="hidden sm:inline group-hover:opacity-0 transition-opacity">Integrations</span>
                  <span className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs">
                    Upgrade to unlock
                  </span>
                </button>
              ) : (
                <Link
                  to="/settings/automation-integrations"
                  onClick={onClose}
                  className="px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.15)] text-gray-300 rounded-lg hover:bg-[rgba(255,255,255,0.1)] transition-colors flex items-center gap-2"
                >
                  <GearSix size={16} />
                  <span className="hidden sm:inline">Integrations</span>
                </Link>
              )}
              
              {/* Show Create Automation Rule CTA for automation-ready agents */}
              {(mode === 'automation_ready' || mode === 'integration_dependent') && (
                isBasicPlan ? (
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="group relative px-4 py-3 bg-[rgba(34,197,94,0.05)] border border-[rgba(34,197,94,0.2)] text-green-600/40 rounded-lg flex items-center gap-2 cursor-pointer hover:bg-[rgba(34,197,94,0.1)] hover:text-green-500/60 transition-all"
                  >
                    <Lightning size={16} />
                    <span className="hidden sm:inline group-hover:opacity-0 transition-opacity">Create Rule</span>
                    <span className="absolute inset-0 hidden sm:flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs text-green-400">
                      Upgrade to unlock
                    </span>
                  </button>
                ) : (
                  <Link
                    to="/settings/automations"
                    onClick={onClose}
                    className="px-4 py-3 bg-[rgba(34,197,94,0.1)] border border-[rgba(34,197,94,0.3)] text-green-400 rounded-lg hover:bg-[rgba(34,197,94,0.2)] transition-colors flex items-center gap-2"
                  >
                    <Lightning size={16} />
                    <span className="hidden sm:inline">Create Rule</span>
                  </Link>
                )
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        currentTier={userPlan || 'basic'}
        message="Unlock Agent Installation"
        feature="Install agents, configure integrations, and create automation rules"
        showComparison={true}
      />
    </>
  );
};

export default AgentDetailModal;
